<script lang="ts" setup>
import {ref, computed} from 'vue'
import {storeToRefs} from 'pinia'
import {DateTime} from 'luxon'
import {useAppStore} from '@/stores/app'
import type {TaskFolder, TaskInstance, TaskWithInstance} from '@backend/types'
import {Checkbox} from '@/components/ui/checkbox'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {TableCell, TableRow} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {MoreHorizontal, Star, Trash2, ArrowRightLeft, FolderOpen, User, Clock3} from 'lucide-vue-next'
import TaskEditDialog from './TaskEditDialog.vue'

const props = defineProps<{
  task: TaskWithInstance
  taskInstance: TaskInstance | null
  viewMode: 'compact' | 'normal' | 'detailed'
}>()

const appStore = useAppStore()
const {projects} = storeToRefs(appStore)

const deleteDialogOpen = ref(false)
const editDialogOpen = ref(false)

const allFolders: TaskFolder[] = ['inbox', 'next', 'scheduled', 'waiting', 'someday', 'reference', 'archived', 'trash']

const dueDate = computed(() => {
  if (props.taskInstance?.due_date) {
    const raw = props.taskInstance.due_date.replace(/ (\d\d:\d\d)/, 'T$1')
    const dt = DateTime.fromISO(raw)
    return dt.isValid ? dt : null
  }
  return null
})

const dueDateFormatted = computed(() => {
  if (!dueDate.value) return null
  const localized = dueDate.value.setLocale('ru')
  if (localized.hour === 0 && localized.minute === 0) {
    return localized.toLocaleString(DateTime.DATE_SHORT)
  }
  return localized.toLocaleString(DateTime.DATETIME_SHORT)
})

const isOverdue = computed(() => {
  if (!dueDate.value) return false
  return dueDate.value < DateTime.now() && !props.taskInstance?.done
})

const projectTitle = computed(() => {
  if (!props.task.project_id) return null
  return projects.value.find((p) => p.project_id === props.task.project_id)?.title ?? null
})

const moveFolders = computed(() => {
  return allFolders.filter((f) => f !== props.task.folder)
})

const hasMultipleInstances = computed(() => {
  return props.task.instances.length > 1
})

async function toggleDone() {
  if (!props.taskInstance) return
  await appStore.updateTaskInstance(props.taskInstance.task_instance_id, {done: !props.taskInstance.done})
}

async function toggleFocus() {
  await appStore.updateTask(props.task.task_id, {focused: !props.task.focused})
}

async function moveToFolder(folder: TaskFolder) {
  await appStore.updateTask(props.task.task_id, {folder})
}

async function confirmDeleteAll() {
  await appStore.deleteTask(props.task.task_id)
  deleteDialogOpen.value = false
}

async function confirmDeleteInstance() {
  if (!props.taskInstance) return
  await appStore.deleteTaskInstance(props.taskInstance.task_instance_id)
  deleteDialogOpen.value = false
}
</script>

<template>
  <TableRow class="group">
    <!-- Checkbox -->
    <TableCell class="w-10 px-3 py-2" @click.stop>
      <Checkbox :model-value="!!taskInstance?.done" :disabled="!taskInstance" @update:model-value="toggleDone" />
    </TableCell>

    <!-- Title + badges + notes -->
    <TableCell class="cursor-pointer overflow-hidden px-2 py-2" @click="editDialogOpen = true">
      <div class="flex items-center gap-1.5">
        <button
          class="shrink-0 text-muted-foreground hover:text-yellow-500"
          :class="{'text-yellow-500': task.focused}"
          @click.stop="toggleFocus">
          <Star class="h-4 w-4" :class="{'fill-current': task.focused}" />
        </button>
        <span class="truncate text-sm" :class="{'line-through text-muted-foreground': taskInstance?.done}">
          {{ task.title }}
        </span>
      </div>

      <!-- Badges row (normal/detailed) -->
      <div v-if="viewMode !== 'compact'" class="mt-1 flex flex-wrap items-center gap-1">
        <Badge v-if="projectTitle" variant="outline" class="gap-1 text-xs">
          <FolderOpen class="h-3 w-3" />
          {{ projectTitle }}
        </Badge>
        <Badge v-for="tag in task.tags" :key="tag" variant="secondary" class="text-xs"> #{{ tag }} </Badge>
        <Badge v-if="task.delegated_to" variant="outline" class="gap-1 text-xs">
          <User class="h-3 w-3" />
          {{ task.delegated_to }}
        </Badge>
        <Badge v-if="task.auto_complete" variant="outline" class="gap-1 text-xs">
          <Clock3 class="h-3 w-3" />
          {{ $t('fields.auto_complete_short') }}
        </Badge>
      </div>

      <!-- Notes preview (detailed only) -->
      <p v-if="viewMode === 'detailed' && task.notes" class="mt-1 line-clamp-2 text-xs text-muted-foreground">
        {{ task.notes }}
      </p>
    </TableCell>

    <!-- Due date + duration (normal/detailed) -->
    <TableCell v-if="viewMode !== 'compact'" class="w-24 px-2 py-2 text-right text-xs text-muted-foreground">
      <span v-if="dueDateFormatted" :class="{'text-red-500 font-medium': isOverdue}">
        {{ dueDateFormatted }}
      </span>
      <div v-if="viewMode === 'detailed' && task.duration" class="mt-0.5">{{ task.duration }} min</div>
    </TableCell>

    <!-- Context menu -->
    <TableCell class="w-10 px-2 py-2" @click.stop>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100">
            <MoreHorizontal class="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-48">
          <!-- Move to... submenu -->
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ArrowRightLeft class="mr-2 h-4 w-4" />
              {{ $t('actions.move') }}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem v-for="folder in moveFolders" :key="folder" @click="moveToFolder(folder)">
                {{ $t('scopes.' + folder) }}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />

          <!-- Trash -->
          <DropdownMenuItem v-if="task.folder !== 'trash'" @click="moveToFolder('trash')">
            <Trash2 class="mr-2 h-4 w-4" />
            {{ $t('actions.trash') }}
          </DropdownMenuItem>

          <!-- Delete -->
          <DropdownMenuItem class="text-destructive focus:text-destructive" @click="deleteDialogOpen = true">
            <Trash2 class="mr-2 h-4 w-4" />
            {{ $t('actions.delete') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  </TableRow>

  <!-- Delete confirmation dialog -->
  <AlertDialog :open="deleteDialogOpen" @update:open="deleteDialogOpen = $event">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ $t('actions.delete') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ hasMultipleInstances ? $t('messages.delete_task_multi') : $t('messages.delete_task_confirm') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{{ $t('actions.cancel') }}</AlertDialogCancel>
        <template v-if="hasMultipleInstances">
          <AlertDialogAction
            class="border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
            @click="confirmDeleteInstance">
            {{ $t('actions.delete_current_instance') }}
          </AlertDialogAction>
          <AlertDialogAction
            class="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            @click="confirmDeleteAll">
            {{ $t('actions.delete_all_instances') }}
          </AlertDialogAction>
        </template>
        <AlertDialogAction v-else @click="confirmDeleteAll">
          {{ $t('actions.delete') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <!-- Edit dialog -->
  <TaskEditDialog
    :task="task"
    :task-instance="taskInstance"
    :open="editDialogOpen"
    @update:open="editDialogOpen = $event" />
</template>
